import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  IndicatorDefinitionRequest,
  IndicatorDefinitionResponse,
  IndicatorValueResponse,
} from "@/types/indicators.types";

const QK = ["indicators"] as const;

export function useIndicators() {
  return useQuery<IndicatorDefinitionResponse[]>({
    queryKey: QK,
    queryFn: () => api.get<IndicatorDefinitionResponse[]>("/api/v1/indicators"),
  });
}

export function useSaveIndicator() {
  const qc = useQueryClient();
  // A tela trata o erro com mensagem própria; sem o opt-out o MutationCache global soma o dele.
  return useMutation({
    meta: { skipErrorToast: true },
    mutationFn: ({ id, payload }: { id: string | null; payload: IndicatorDefinitionRequest }) =>
      id
        ? api.patch<IndicatorDefinitionResponse>(`/api/v1/indicators/${id}`, payload)
        : api.post<IndicatorDefinitionResponse>("/api/v1/indicators", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useDeleteIndicator() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipErrorToast: true },
    mutationFn: (id: string) => api.delete<void>(`/api/v1/indicators/${id}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

/**
 * Spec 082 (FR-029) — o valor aparece ANTES de salvar, e recalcula a cada frase. Salvar não é o
 * que faz o número aparecer, então isto é `useMutation` e não `useQuery`: é a tela pedindo um
 * cálculo para um rascunho que ainda não existe no servidor.
 */
export function useIndicatorPreview() {
  return useMutation({
    mutationFn: (body: { definition: IndicatorDefinitionRequest; from: string; to: string }) =>
      api.post<IndicatorValueResponse>("/api/v1/indicators/preview", body),
  });
}
